/**
 * GameStateService - Manages the authoritative game state received from the backend.
 *
 * This service:
 * - Stores the current game state from server messages
 * - Provides accessors for UI components to read state
 * - Supports state change listeners for reactive updates
 *
 * The frontend never computes game state; it only reflects what the server sends.
 */

import {
  ServerToClientMessage,
  GameStateUpdate,
  MatchStarted,
  PlayerData,
  RoundStarted,
  RoundTransition,
  GameOver,
  CombatStarted,
  CombatResult,
  CombatStream,
  QueueStatusUpdate,
  PreparationTimerUpdate,
  ReadyStateUpdate,
  ErrorResponse,
  ReconnectState,
  SpectatorCombatSelected,
  SitOutNotification,
  CombatAttackEvent,
  CombatDamageEvent,
  CombatDeathEvent,
  CombatKillGoldEvent,
  CombatAbilityEvent,
  UpgradeOptionsResponse,
  CoopLobbyStateUpdate,
  LobbyStateUpdate,
  type UpgradeOption,
  type CoopMatchInfo,
  type CardFxEvent,
} from '../../shared/messages/MessageContracts';
import { type QueueMode } from '../../shared/models/QueueModes';

// ─── Queue State ────────────────────────────────────────────────────────────

export interface QueueState {
  isInQueue: boolean;
  /** The viewer's own queue ('fast' | 'long'), or null when not queued. */
  queueMode: QueueMode | null;
  /** Players in the VIEWER'S own queue (0 when not queued). */
  playersInQueue: number;
  /** More players needed in the VIEWER'S own queue (0 when not queued). */
  playersNeeded: number;
  /** Number of players in the FAST queue (both queues shown in the view). */
  fastQueuePlayers: number;
  /** Number of players in the LONG queue. */
  longQueuePlayers: number;
  /**
   * Seconds until the match auto-starts with bots (authoritative server
   * countdown). Present from 2 s after a join until the timer fires (the
   * rest of the 30 s window); absent (undefined) during the first 2 s after
   * a timer reset (new join) or once the match starts — the queue view
   * hides its "Starting with Bots in: X seconds" hint while absent.
   */
  botFillSecondsLeft?: number;
}

// ─── Match State ────────────────────────────────────────────────────────────

export interface MatchState {
  matchId: string | null;
  currentRound: number;
  phase: string;
  players: PlayerData[];
  gameOver: boolean;
  winnerId: string | null;
  preparationTimerSeconds: number | null;
  /** Whether preparation actions are locked (dead-time threshold before combat). */
  preparationLocked: boolean;
  /**
   * Preparation "ready" state (authoritative, from ReadyStateUpdate):
   * the IDs of all players currently ready (alive + in preparation). An
   * empty list = nobody is ready (the fresh-preparation reset).
   */
  readyPlayerIds: string[];
  /** Number of living players in preparation (the "n" of "x/n"). 0 outside preparation. */
  readyTotalPlayers: number;
  isSpectating: boolean;
  spectatorCombatPlayerAId: string | null;
  spectatorCombatPlayerBId: string | null;
  /**
   * The authoritative sit-out gold notification for the current round (the
   * amount awarded to the sitting-out player for spectating). Null outside
   * a sit-out round (cleared on RoundStarted).
   */
  sitOutNotification: SitOutNotification | null;
  lastError: string | null;
  lastCombatAttack: CombatAttackEvent | null;
  lastCombatDamage: CombatDamageEvent | null;
  lastCombatDeath: CombatDeathEvent | null;
  lastCombatKillGold: CombatKillGoldEvent | null;
  lastCombatAbility: CombatAbilityEvent | null;
  lastCombatResult: CombatResult | null;
  combatStarted: CombatStarted | null;
  roundTransition: RoundTransition | null;
  /**
   * The COOP match info (null for regular matches): mode / teamOrder /
   * host+teammate ids / enemy bot ids / mission. Drives the coop team
   * board layout (the two fixed team halves + the enemy row), the yellow
   * teammate overlay, the split sell/give zone and the shared life HUD.
   */
  coop: CoopMatchInfo | null;
  /** Server-provided upgrade options for the currently selected minion. */
  upgradeOptions: UpgradeOption[] | null;
  upgradeOptionsMinionId: string | null;
  upgradeCost: number | null;
  /** Batched combat event stream (replaces per-event streaming). */
  combatStream: CombatStream | null;
  /**
   * One-shot CARD FX events attached to the latest GameStateUpdate
   * (task 186 — 'overdraw': a draw lost to a full hand → red card-like
   * explosion over that player's hand; 'cardDestroyed': the Sacrificial
   * Rune evaporated a random hand card → burning particles at its last
   * position). The hand renderer consumes a given array reference exactly
   * once (a fresh array per server update; timer ticks keep the same
   * reference, so the FX never replays).
   */
  cardEvents: CardFxEvent[];
  /** Flag indicating this state change contains new combat event data.
   * Set to true when combatStarted, combatStream, or individual combat
   * events (attack/damage/death/gold/ability/result) are updated.
   * Consumers can skip combat event processing when false,
   * avoiding unnecessary work on timer ticks and HUD updates. */
  isCombatEvent: boolean;
}

// ─── COOP Lobby State ───────────────────────────────────────────────────────

export type CoopLobbyRole = 'host' | 'member';

/**
 * The viewer's COOP lobby (the authoritative CoopLobbyStateUpdate, mirrored
 * locally). Null when the viewer is not in a lobby.
 */
export interface CoopLobbyClientState {
  lobbyCode: string;
  hostId: string;
  hostName: string;
  memberId: string | null;
  memberName: string | null;
  missionId: string;
  availableMissionIds: string[];
  /** The host's NEXT mission to complete (null = all cleared). */
  nextMissionId: string | null;
  /**
   * The host's chosen combat side ('left' → the host's minions first/
   * left, first to attack; 'right' → the teammate's). Legacy lobbies
   * (no field) default to 'left'.
   */
  hostSide: 'left' | 'right';
  /** False = the lobby was removed (return to the menu). */
  exists: boolean;
  /** The viewer's role in this lobby (null when not a member). */
  viewerRole: CoopLobbyRole | null;
}

// ─── Full Application State ─────────────────────────────────────────────────

/**
 * The viewer's UNIFIED lobby (the authoritative LobbyStateUpdate, mirrored
 * locally). Covers BOTH original and co-op lobbies. Null when the viewer
 * is not in a lobby. This is the single source the lobby screen renders
 * from (the canonical snapshot — no client-side divergence).
 */
export interface LobbyClientState {
  id: string;
  code: string;
  hostUserId: string;
  name: string;
  gameType: 'original' | 'coop';
  visibility: 'public' | 'private';
  ranked: boolean;
  playerCapacity: number;
  originalSize: 4 | 8 | null;
  /** 'original' only (task 209): the players-starting-life. null for coop. */
  startingLife: number | null;
  state: 'WAITING' | 'COUNTDOWN' | 'IN_GAME' | 'POST_GAME' | 'CLOSED';
  countdownStartedAt: number | null;
  countdownEndsAt: number | null;
  /** The countdown's remaining SECONDS (server-computed; COUNTDOWN only). */
  countdownSecondsLeft?: number;
  players: { userId: string; displayName: string; joinedAt: number; joinedVia: string; isHost: boolean }[];
  // Co-op only:
  missionId?: string;
  availableMissionIds?: string[];
  nextMissionId?: string | null;
  hostSide?: 'left' | 'right';
  exists: boolean;
  /** The viewer's role in this lobby (null when not a member). */
  viewerRole: 'host' | 'member' | null;
}

export interface AppGamestate {
  queue: QueueState;
  match: MatchState;
  coopLobby: CoopLobbyClientState | null;
  lobby: LobbyClientState | null;
}

/**
 * Default initial queue state.
 */
export function createDefaultQueueState(): QueueState {
  return {
    isInQueue: false,
    queueMode: null,
    playersInQueue: 0,
    playersNeeded: 0,
    fastQueuePlayers: 0,
    longQueuePlayers: 0,
  };
}

/**
 * Default initial match state.
 */
export function createDefaultMatchState(): MatchState {
  return {
    matchId: null,
    currentRound: 0,
    phase: '',
    players: [],
    gameOver: false,
    winnerId: null,
    preparationTimerSeconds: null,
    preparationLocked: false,
    readyPlayerIds: [],
    readyTotalPlayers: 0,
    isSpectating: false,
    spectatorCombatPlayerAId: null,
    spectatorCombatPlayerBId: null,
    sitOutNotification: null,
    lastError: null,
    lastCombatAttack: null,
    lastCombatDamage: null,
    lastCombatDeath: null,
    lastCombatKillGold: null,
    lastCombatAbility: null,
    lastCombatResult: null,
    isCombatEvent: false,
    combatStarted: null,
    combatStream: null,
    cardEvents: [],
    roundTransition: null,
    coop: null,
    upgradeOptions: null,
    upgradeOptionsMinionId: null,
    upgradeCost: null,
  };
}

/**
 * Default initial full application state.
 */
export function createDefaultGameState(): AppGamestate {
  return {
    queue: createDefaultQueueState(),
    match: createDefaultMatchState(),
    coopLobby: null,
    lobby: null,
  };
}

// ─── State Change Listener ──────────────────────────────────────────────────

export type GameStateChangeListener = (state: AppGamestate) => void;

// ─── GameStateService ───────────────────────────────────────────────────────

export class GameStateService {
  private _state: AppGamestate;
  private _listeners: GameStateChangeListener[] = [];
  /** The viewer's player id (for role attribution in the coop lobby). */
  private _viewerId: string | null = null;

  constructor(viewerId?: string) {
    this._state = createDefaultGameState();
    this._viewerId = viewerId ?? null;
  }

  /** Sets the viewer id (used for the coop lobby's viewerRole). */
  setViewerId(playerId: string): void {
    this._viewerId = playerId;
  }

  // ─── Public State Accessors ──────────────────────────────────────────────

  /** Returns a copy of the current full game state. */
  getState(): AppGamestate {
    return this._shallowCopyState(this._state);
  }

  /** Returns the current queue state. */
  getQueueState(): QueueState {
    return { ...this._state.queue };
  }

  /** Returns the current match state. */
  getMatchState(): MatchState {
    return { ...this._state.match };
  }

  /** Returns the current player's data (by playerId). */
  getPlayerData(playerId: string): PlayerData | null {
    return this._state.match.players.find((p) => p.id === playerId) ?? null;
  }

  /** Returns all players' data. */
  getAllPlayers(): PlayerData[] {
    return this._state.match.players.map((p) => ({ ...p }));
  }

  /** Returns true if a match is currently active. */
  get isInMatch(): boolean {
    return this._state.match.matchId !== null && !this._state.match.gameOver;
  }

  /** Returns true if the game is over. */
  get isGameOver(): boolean {
    return this._state.match.gameOver;
  }

  /** Returns true if the player is in the queue. */
  get isInQueue(): boolean {
    return this._state.queue.isInQueue;
  }

  /** Returns the viewer's coop lobby state (null when not in a lobby). */
  getCoopLobbyState(): CoopLobbyClientState | null {
    return this._state.coopLobby;
  }

  /** Returns the viewer's unified lobby state (null when not in a lobby). */
  getLobbyState(): LobbyClientState | null {
    return this._state.lobby;
  }

  // ─── State Change Listeners ──────────────────────────────────────────────

  /**
   * Subscribe to state changes.
   * Returns an unsubscribe function.
   */
  onStateChange(listener: GameStateChangeListener): () => void {
    this._listeners.push(listener);
    return () => {
      const index = this._listeners.indexOf(listener);
      if (index !== -1) {
        this._listeners.splice(index, 1);
      }
    };
  }

  // ─── Message Processing ──────────────────────────────────────────────────

  /**
   * Process an incoming server message and update state.
   * This is the single entry point for state mutations.
   */
  processMessage(message: ServerToClientMessage): void {
    switch (message.messageType) {
      case 'QueueStatusUpdate':
        this._handleQueueStatusUpdate(message);
        break;
      case 'MatchStarted':
        this._handleMatchStarted(message);
        break;
      case 'GameStateUpdate':
        this._handleGameStateUpdate(message);
        break;
      case 'PreparationTimerUpdate':
        this._handlePreparationTimerUpdate(message);
        break;
      case 'ReadyStateUpdate':
        this._handleReadyStateUpdate(message);
        break;
      case 'CombatStarted':
        this._handleCombatStarted(message);
        break;
      case 'CombatStream':
        this._handleCombatStream(message);
        break;
      case 'CombatAttackEvent':
        this._handleCombatAttackEvent(message);
        break;
      case 'CombatDamageEvent':
        this._handleCombatDamageEvent(message);
        break;
      case 'CombatDeathEvent':
        this._handleCombatDeathEvent(message);
        break;
      case 'CombatKillGoldEvent':
        this._handleCombatKillGoldEvent(message);
        break;
      case 'CombatAbilityEvent':
        this._handleCombatAbilityEvent(message);
        break;
      case 'CombatResult':
        this._handleCombatResult(message);
        break;
      case 'RoundStarted':
        this._handleRoundStarted(message);
        break;
      case 'RoundTransition':
        this._handleRoundTransition(message);
        break;
      case 'GameOver':
        this._handleGameOver(message);
        break;
      case 'SpectatorCombatSelected':
        this._handleSpectatorCombatSelected(message);
        break;
      case 'SitOutNotification':
        this._handleSitOutNotification(message);
        break;
      case 'ReconnectState':
        this._handleReconnectState(message);
        break;
      case 'UpgradeOptionsResponse':
        this._handleUpgradeOptionsResponse(message);
        break;
      case 'ErrorResponse':
        this._handleErrorResponse(message);
        break;
      case 'Authenticated':
      case 'AuthFailed':
        // Connection-level auth messages are handled by AppShell (shell
        // emits authChange/authFailed events). They are not part of match
        // state, so nothing is stored here.
        break;
      case 'AchievementsResponse':
      case 'AchievementUnlocked':
        // Session-level achievements messages are intercepted by AppShell
        // (achievements overlay / unlock toast). They are not part of match
        // state, so nothing is stored here.
        break;
      case 'StatsResponse':
        // Session-level statistics message is intercepted by AppShell
        // (stats overlay). It is not part of match state, so nothing is
        // stored here.
        break;
      case 'SkinsResponse':
        // Session-level skins message is intercepted by AppShell (skins
        // overlay). It is not part of match state, so nothing is stored
        // here — per-player skin selections ride along on the PlayerData
        // of regular state updates / CombatStarted instead.
        break;
      case 'ToplistResponse':
        // Session-level toplist message is intercepted by AppShell
        // (toplist overlay). It is not part of match state, so nothing is
        // stored here.
        break;
      case 'CoopLobbyStateUpdate':
        this._handleCoopLobbyStateUpdate(message);
        break;
      case 'LobbyStateUpdate':
        this._handleLobbyStateUpdate(message);
        break;
      default: {
        const _exhaustiveCheck: never = message;
        console.warn(`GameStateService: Unknown message type "${(_exhaustiveCheck as any).messageType}"`);
      }
    }

    this._notifyListeners();
  }

  /**
   * Reset all state to defaults (e.g., on disconnect).
   */
  reset(): void {
    this._state = createDefaultGameState();
    this._notifyListeners();
  }

  // ─── Message Handlers ────────────────────────────────────────────────────

  /**
   * Handle the authoritative COOP lobby state. The lobby screen renders
   * purely from this (the invite code, the mission text, the seat
   * status). `exists: false` (host left / match started) clears the
   * lobby and returns the client to the menu.
   */
  private _handleCoopLobbyStateUpdate(msg: CoopLobbyStateUpdate): void {
    if (!msg.exists) {
      this._state.coopLobby = null;
      return;
    }
    const viewerRole =
      this._viewerId === msg.hostId
        ? 'host'
        : this._viewerId === msg.memberId
          ? 'member'
          : null;
    this._state.coopLobby = {
      lobbyCode: msg.lobbyCode,
      hostId: msg.hostId,
      hostName: msg.hostName,
      memberId: msg.memberId,
      memberName: msg.memberName,
      missionId: msg.missionId,
      availableMissionIds: [...msg.availableMissionIds],
      // Legacy messages omit the newer fields — safe defaults.
      nextMissionId: msg.nextMissionId === undefined ? null : msg.nextMissionId,
      hostSide: msg.hostSide === undefined ? 'left' : msg.hostSide,
      exists: true,
      viewerRole,
    };
  }

  /**
   * Handle the authoritative UNIFIED lobby state. The lobby screen renders
   * purely from this. `exists: false` (lobby closed / host left) clears the
   * lobby and returns the client to the menu. A match start (MatchStarted)
   * also clears the lobby (the players enter the match).
   */
  private _handleLobbyStateUpdate(msg: LobbyStateUpdate): void {
    if (!msg.exists) {
      this._state.lobby = null;
      return;
    }
    const viewerRole =
      this._viewerId === msg.hostUserId
        ? 'host'
        : msg.players.some((p) => p.userId === this._viewerId)
          ? 'member'
          : null;
    // No longer a member (we left / were kicked — the server notifies the
    // leaver with the new snapshot): clear the lobby, back to the menu.
    if (viewerRole === null) {
      this._state.lobby = null;
      return;
    }
    this._state.lobby = {
      id: msg.id,
      code: msg.code,
      hostUserId: msg.hostUserId,
      name: msg.name,
      gameType: msg.gameType,
      visibility: msg.visibility,
      ranked: msg.ranked,
      playerCapacity: msg.playerCapacity,
      originalSize: msg.originalSize,
      startingLife: msg.startingLife ?? null,
      state: msg.state,
      countdownStartedAt: msg.countdownStartedAt,
      countdownEndsAt: msg.countdownEndsAt,
      ...(msg.countdownSecondsLeft !== undefined
        ? { countdownSecondsLeft: msg.countdownSecondsLeft }
        : {}),
      players: msg.players.map((p) => ({ ...p })),
      missionId: msg.missionId,
      availableMissionIds: msg.availableMissionIds ? [...msg.availableMissionIds] : undefined,
      nextMissionId: msg.nextMissionId,
      hostSide: msg.hostSide,
      exists: true,
      viewerRole,
    };
  }

  private _handleQueueStatusUpdate(msg: QueueStatusUpdate): void {
    this._state.queue = {
      // Authoritative per-player membership from the server. The broadcast
      // is sent to ALL waiting clients — including the player who just
      // LEFT the queue — so unconditionally assuming "in queue" here would
      // keep showing "Leave Queue" after a leave (the old bug). Legacy
      // messages without the field keep the historical assumption.
      isInQueue: msg.isInQueue ?? true,
      // The viewer's own queue mode (authoritative; null = not queued).
      queueMode: msg.queueMode ?? null,
      playersInQueue: msg.playersInQueue,
      playersNeeded: msg.playersNeeded,
      // Both queues' sizes for the queue view (legacy messages omit them —
      // the view hides the per-queue rows then).
      fastQueuePlayers: msg.fastQueuePlayers ?? 0,
      longQueuePlayers: msg.longQueuePlayers ?? 0,
      // Absent from the message = outside the countdown window (or legacy
      // message) → drop the field so the hint hides (timer was just reset).
      ...(msg.botFillSecondsLeft !== undefined
        ? { botFillSecondsLeft: msg.botFillSecondsLeft }
        : {}),
    };
  }

  private _handleMatchStarted(msg: MatchStarted): void {
    this._state.queue.isInQueue = false;
    // The bot-fill countdown is over (the match is starting) — hide the hint.
    delete this._state.queue.botFillSecondsLeft;
    // A match started from the lobby consumes the lobby screen (both clients
    // leave the lobby UI and enter the match). The LOBBY itself survives on
    // the server (IN_GAME → POST_GAME) — only the client's lobby SCREEN is
    // cleared; it returns on the next LobbyStateUpdate (POST_GAME).
    this._state.coopLobby = null;
    this._state.lobby = null;
    this._state.match = {
      ...createDefaultMatchState(),
      matchId: msg.matchId,
      currentRound: msg.currentRound,
      phase: msg.phase,
      players: msg.players,
      coop: msg.coop ?? null,
    };
  }

  private _handleGameStateUpdate(msg: GameStateUpdate): void {
    // Clear combatStarted when phase returns to preparation (combat ended)
    const clearCombatStarted = msg.phase === 'preparation' ? null : this._state.match.combatStarted;
    // Reset preparation lock when returning to preparation phase
    const resetLock = msg.phase === 'preparation' ? false : this._state.match.preparationLocked;
    // Drop the consumed combat stream when the post-combat state update
    // arrives, so stale stream data cannot re-trigger combat processing.
    const clearCombatStream = msg.phase === 'preparation' ? null : this._state.match.combatStream;
    // Drop the "waiting for other combats" notification when the round has
    // advanced — the post-combat state update (phase='preparation') is the
    // authoritative end of the wait window the RoundTransition described.
    const clearRoundTransition = msg.phase === 'preparation' ? null : this._state.match.roundTransition;

    this._state.match = {
      ...this._state.match,
      matchId: msg.matchId,
      currentRound: msg.currentRound,
      phase: msg.phase,
      players: msg.players,
      gameOver: msg.gameOver,
      winnerId: msg.winnerId,
      combatStarted: clearCombatStarted,
      combatStream: clearCombatStream,
      roundTransition: clearRoundTransition,
      preparationLocked: resetLock,
      // One-shot card FX events (task 186) — a FRESH array per update (the
      // server drains them), so the renderer's reference guard consumes
      // each event batch exactly once.
      cardEvents: msg.cardEvents ?? [],
      // The coop info is constant for the match; the server repeats it on
      // every update (legacy/non-coop messages omit it → keep the prior).
      coop: msg.coop ?? this._state.match.coop,
      // The cached upgrade options belong to the board state at the moment
      // the server computed them — any authoritative board change (this
      // update, e.g. the player's own upgrade just landing) makes them
      // stale. Clear them so the next panel opening waits for a fresh
      // options response instead of rendering the previous state (the
      // "same ability offered again after upgrading the same minion" bug —
      // the panel used to show the previous response's options, which
      // still listed the ability the minion had just gained).
      upgradeOptions: null,
      upgradeOptionsMinionId: null,
    };
  }

  private _handlePreparationTimerUpdate(msg: PreparationTimerUpdate): void {
    this._state.match.preparationTimerSeconds = msg.remainingSeconds;
    if (msg.isLocked !== undefined) {
      this._state.match.preparationLocked = msg.isLocked;
    }
  }

  /**
   * Stores the authoritative preparation ready state. A ReadyStateUpdate
   * with an empty readyPlayerIds list IS the per-round reset (the server
   * broadcasts it at the start of every preparation phase — that is what
   * resets the Ready button to "not ready" after combat). Updates for a
   * different match are ignored (stale broadcast safety).
   */
  private _handleReadyStateUpdate(msg: ReadyStateUpdate): void {
    if (this._state.match.matchId && msg.matchId !== this._state.match.matchId) return;
    this._state.match.readyPlayerIds = [...msg.readyPlayerIds];
    this._state.match.readyTotalPlayers = msg.totalPlayers;
  }

  private _handleCombatStarted(msg: CombatStarted): void {
    this._state.match.combatStarted = msg;
    // The server has entered the combat phase for this match — reflect it
    // so the HUD/phase-driven UI stay consistent during the animation window.
    this._state.match.phase = 'combat';
    this._state.match.isCombatEvent = true;
    // Clear stale combat event fields from any previous combat so they
    // are not re-processed when the new combat begins.
    this._state.match.lastCombatAttack = null;
    this._state.match.lastCombatDamage = null;
    this._state.match.lastCombatDeath = null;
    this._state.match.lastCombatKillGold = null;
    this._state.match.lastCombatAbility = null;
    this._state.match.lastCombatResult = null;
    this._state.match.combatStream = null;
  }

  /**
   * Handles a batched CombatStream message containing all combat events.
   * The frontend animates events locally with its own timing delays.
   */
  private _handleCombatStream(msg: CombatStream): void {
    this._state.match.combatStream = msg;
    this._state.match.isCombatEvent = true;
    // Also update the lastCombatResult so the result UI picks it up
    const resultEvent = msg.events.find(
      (e) => e.messageType === 'CombatResult'
    ) as import('../../shared/messages/MessageContracts').CombatStreamEvent & { messageType: 'CombatResult' } | undefined;
    if (resultEvent) {
      this._state.match.lastCombatResult = {
        version: msg.version,
        messageType: 'CombatResult',
        matchId: msg.matchId,
        winnerId: resultEvent.winnerId,
        loserId: resultEvent.loserId,
        isTie: resultEvent.isTie,
        isTimeout: resultEvent.isTimeout,
        lifeLost: resultEvent.lifeLost,
        goldGained: resultEvent.goldGained,
      };
    }
  }

  private _handleCombatAttackEvent(msg: CombatAttackEvent): void {
    this._state.match.lastCombatAttack = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleCombatDamageEvent(msg: CombatDamageEvent): void {
    this._state.match.lastCombatDamage = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleCombatDeathEvent(msg: CombatDeathEvent): void {
    this._state.match.lastCombatDeath = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleCombatKillGoldEvent(msg: CombatKillGoldEvent): void {
    this._state.match.lastCombatKillGold = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleCombatAbilityEvent(msg: CombatAbilityEvent): void {
    this._state.match.lastCombatAbility = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleCombatResult(msg: CombatResult): void {
    this._state.match.lastCombatResult = msg;
    this._state.match.isCombatEvent = true;
  }

  private _handleRoundStarted(msg: RoundStarted): void {
    this._state.match.currentRound = msg.currentRound;
    this._state.match.phase = msg.phase;
    this._state.match.preparationTimerSeconds = msg.preparationTimerSeconds;
    // Reset preparation lock for the new round
    this._state.match.preparationLocked = false;
    // Clear any pending round transition when a new round starts
    this._state.match.roundTransition = null;
    // Spectating is PER-ROUND state: a new preparation round re-arms it
    // only when the server sends a fresh SpectatorCombatSelected (the
    // player sits out again) — a stale 'watching' state from the previous
    // round would otherwise keep the Spectating banner / result-overlay
    // suppression alive for a combat the player takes part in.
    this._state.match.isSpectating = false;
    this._state.match.spectatorCombatPlayerAId = null;
    this._state.match.spectatorCombatPlayerBId = null;
    this._state.match.sitOutNotification = null;
  }

  private _handleRoundTransition(msg: RoundTransition): void {
    this._state.match.roundTransition = msg;
  }

  private _handleGameOver(msg: GameOver): void {
    this._state.match.gameOver = true;
    this._state.match.winnerId = msg.winnerId;
    this._state.match.players = msg.finalPlayers;
  }

  private _handleSpectatorCombatSelected(msg: SpectatorCombatSelected): void {
    this._state.match.isSpectating = true;
    this._state.match.spectatorCombatPlayerAId = msg.combatPlayerAId;
    this._state.match.spectatorCombatPlayerBId = msg.combatPlayerBId;
  }

  private _handleSitOutNotification(msg: SitOutNotification): void {
    // The sit-out gold is reflected in the next GameStateUpdate, but the
    // notification itself is kept so the spectator UI can show the exact
    // amount (SpectatorModeUI reads match.sitOutNotification).
    this._state.match.sitOutNotification = msg;
  }

  private _handleReconnectState(msg: ReconnectState): void {
    this._state.match = {
      ...createDefaultMatchState(),
      matchId: msg.matchId,
      currentRound: msg.currentRound,
      phase: msg.phase,
      players: msg.players,
      gameOver: msg.gameOver,
      winnerId: msg.winnerId,
      // Mid-preparation reconnects carry the current ready state (the server
      // enriches ReconnectState while a preparation timer is active); absent
      // fields fall back to the "not ready" defaults.
      readyPlayerIds: msg.readyPlayerIds ? [...msg.readyPlayerIds] : [],
      readyTotalPlayers: msg.readyTotalPlayers ?? 0,
      coop: msg.coop ?? null,
    };
  }

  private _handleUpgradeOptionsResponse(msg: UpgradeOptionsResponse): void {
    this._state.match.upgradeOptions = msg.options;
    this._state.match.upgradeOptionsMinionId = msg.minionInstanceId;
    this._state.match.upgradeCost = msg.upgradeCost;
  }

  private _handleErrorResponse(msg: ErrorResponse): void {
    this._state.match.lastError = msg.error;
  }

  // ─── Internal Utilities ──────────────────────────────────────────────────

  private _notifyListeners(): void {
    const state = this._shallowCopyState(this._state);
    for (const listener of this._listeners) {
      listener(state);
    }
    // Reset isCombatEvent after notifying so it doesn't persist
    // across subsequent non-combat state changes (timer ticks, etc.)
    this._state.match.isCombatEvent = false;
  }

  /**
   * Creates a defensive copy of the state using shallow copies + selective
   * deep copies only for mutable arrays. Avoids expensive JSON serialization.
   *
   * - Top-level: shallow copy of AppGamestate
   * - Queue: shallow copy (simple primitives)
   * - Match: shallow copy of MatchState fields
   * - Players: new array with each player shallow-copied, hand/battlefield arrays cloned
   * - Combat events: shallow copy (replaced entirely on each update, never mutated in place)
   */
  private _shallowCopyState(state: AppGamestate): AppGamestate {
    return {
      queue: { ...state.queue },
      match: {
        ...state.match,
        players: state.match.players.map((p) => ({
          ...p,
          hand: [...p.hand],
          battlefield: p.battlefield.map((m) => ({ ...m, abilities: [...m.abilities] })),
        })),
        // Combat event fields are replaced entirely on each update,
        // so shallow copy is safe (no in-place mutation).
        upgradeOptions: state.match.upgradeOptions ? state.match.upgradeOptions.map((o) => ({ ...o })) : null,
        combatStream: state.match.combatStream
          ? { ...state.match.combatStream, events: [...state.match.combatStream.events] }
          : null,
      },
      // The lobby state is replaced entirely on each update (never mutated
      // in place) — a shallow copy is safe.
      coopLobby: state.coopLobby ? { ...state.coopLobby, availableMissionIds: [...state.coopLobby.availableMissionIds] } : null,
      lobby: state.lobby
        ? {
            ...state.lobby,
            players: state.lobby.players.map((p) => ({ ...p })),
            availableMissionIds: state.lobby.availableMissionIds
              ? [...state.lobby.availableMissionIds]
              : undefined,
          }
        : null,
    };
  }
}
